import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '@/theme/scalingTokens';

export const useCalendarScreenStyles = makeStyles({
  /* Page wrapper — fills the planner content area; only the grid scrolls */
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    paddingTop: '12px',
    paddingBottom: tokens.spacingVerticalXL,
    paddingLeft: '32px',
    paddingRight: '32px',
    backgroundColor: tokens.colorNeutralBackground2,
    boxSizing: 'border-box',
    [mq.tablet]: {
      paddingLeft: tokens.spacingHorizontalS,
      paddingRight: tokens.spacingHorizontalS,
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },

  /* Title block: stacked title + subtitle */
  pageHeader: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '8px',
  },
  pageTitle: {
    display: 'block',
    fontSize: fontScale.title,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '28px',
    color: tokens.colorNeutralForeground1,
  },
  pageSubtitle: {
    display: 'block',
    fontSize: fontScale.body,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },

  /* ── Tabs ── */
  tabList: {
    marginBottom: '4px',
    ontSize: fontScale.body,
    '& .fui-Tab__content': {
      fontSize: fontScale.body,
    },
    '&[aria-selected="true"] .fui-Tab__content': {
      fontWeight: tokens.fontWeightSemibold,
    },
    '& .fui-Tab__content--reserved-space': {
      fontSize: fontScale.body,
      fontWeight: tokens.fontWeightSemibold,
    },
  },

  /* ── Unified white card ── */
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    // Floor for very short viewports; the page scrolls only below this.
    minHeight: '22.5rem',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow2,
    overflow: 'hidden',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },

  /* Toolbar — single row, 48px height, same bg as sidebar (inverted-L) */
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    minHeight: '48px',
    flexShrink: 0,
    paddingLeft: '13px',
    paddingRight: '13px',
    backgroundColor: tokens.colorNeutralBackground3,
    flexWrap: 'wrap',
    [mq.tablet]: {
      ...shorthands.gap('4px'),
      paddingLeft: '8px',
      paddingRight: '8px',
      paddingTop: '4px',
      paddingBottom: '4px',
    },
  },
  toolbarSpacer: {
    flex: 1,
  },

  /* Today button — white bg, 1px border, 4px radius, 32px height (Figma) */
  todayButton: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('0px', '12px'),
    height: '32px',
    minHeight: '32px',
    maxHeight: '32px',
    minWidth: 'auto',
    fontSize: fontScale.body,
    lineHeight: '30px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    boxSizing: 'border-box' as const,
    display: 'flex',
    alignItems: 'center',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navButton: {
    minWidth: 'auto',
    ...shorthands.padding('0px', '4px'),
  },

  /* Date label as a clickable button with chevron (Figma: "Sep 6 – 12, 2026 ∨") */
  navLabelButton: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    ...shorthands.border('none'),
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navLabelChevron: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground3,
  },

  /* Filter group: icon + "Filter" text — styled as single white bordered button */
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('0px', '12px'),
    height: '32px',
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
    fontSize: fontScale.body,
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  filterIcon: {
    fontSize: fontScale.subtitle,
    color: tokens.colorNeutralForeground1,
  },
  filterDropdown: {
    minWidth: '0px',
    ...shorthands.border('none'),
    backgroundColor: 'transparent',
    ...shorthands.padding('0px'),
    '& button': {
      ...shorthands.border('none'),
      backgroundColor: 'transparent',
      ...shorthands.padding('0px'),
      minWidth: 'auto',
      height: 'auto',
      fontSize: fontScale.body,
      fontWeight: '600',
      color: tokens.colorNeutralForeground1,
    },
  },

  /* View dropdown — white bordered button, 32px, semibold 14px */
  viewDropdown: {
    minWidth: 'auto',
    ...shorthands.border('none'),
    ...shorthands.padding('0px'),
    '& button.fui-Dropdown__clearButton': {
      display: 'none',
    },
    '& button': {
      backgroundColor: tokens.colorNeutralBackground1,
      ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
      ...shorthands.borderRadius('4px'),
      ...shorthands.padding('0px', '12px'),
      height: '32px',
      minHeight: '32px',
      maxHeight: '32px',
      fontSize: fontScale.body,
      lineHeight: '30px',
      fontWeight: '600',
      color: tokens.colorNeutralForeground1,
      boxSizing: 'border-box' as const,
      display: 'flex',
      alignItems: 'center',
    },
  },

  /* ── Body: mini calendar + grid ── */
  bodyLayout: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    position: 'relative' as const,
    [mq.tablet]: {
      flexDirection: 'column',
    },
  },
  sidebar: {
    width: '190px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('12px', '10px'),
    [mq.tablet]: {
      display: 'none',
    },
  },
  calendarArea: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative' as const,
  },
  calendarWrapper: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'auto',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    position: 'relative' as const,
    [mq.tablet]: {
      borderLeft: 'none',
    },
    // The package gives the sticky day header and the now-line z-index 3. Set the line to 2 so it sits below the header and above events (1).
    '& [data-testid="time-grid-current-time"]': {
      zIndex: 2,
    },
  },
  loadingOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    zIndex: 10,
  },

  filterButtonActive: {
    borderBottom: `2px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
  },
});
